import React, { useState } from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import { useGlobalPaywall } from '../hooks/useGlobalPaywall';
import ProtectedRoute from './ProtectedRoute';
import { MainLayout } from '../layouts';
import PromoCodeModal from './PromoCodeModal';

interface SubscriptionRouteProps {
  children: React.ReactNode;
  trackId?: string; // Optional track ID, if not provided, will try to extract from URL params
  requireSubscription?: boolean; // If true, requires active subscription, if false, just checks authentication
}

/**
 * SubscriptionRoute component that enforces subscription-based access control
 * Built on top of ProtectedRoute for authentication + subscription access
 * 
 * Usage:
 * <Route path="/tips/:state/:track/:date" element={<SubscriptionRoute><TipsPage /></SubscriptionRoute>} />
 */
const SubscriptionRoute: React.FC<SubscriptionRouteProps> = ({ 
  children, 
  trackId: providedTrackId,
  requireSubscription = true
}) => {
  const { isAuthenticated } = useAuth();
  const { shouldEnforcePaywall, shouldEnforcePaywallForDate, isPaywallTestUser, globalPaywallEnabled } = useGlobalPaywall();
  const location = useLocation();
  const params = useParams();
  
  // Extract track ID and date from URL params if not provided
  // URL format: /:state/:track/:date
  const trackIdFromUrl = params.track;
  const dateFromUrl = params.date;
  const finalTrackId = providedTrackId || trackIdFromUrl;

  // For non-track pages (e.g. Tippa's Picks), always enforce the paywall regardless of the global setting.
  // For track pages, enforce based on date or the global paywall setting.
  const finalShouldEnforcePaywall = !finalTrackId
    ? true
    : dateFromUrl ? shouldEnforcePaywallForDate(dateFromUrl) : shouldEnforcePaywall;
  
  // Get subscription access status
  const { 
    hasAccess, 
    isLoading: subscriptionLoading, 
    isSubscribed,
    activeSubscriptions 
  } = useSubscriptionAccess(finalTrackId);
  
  console.log('SubscriptionRoute Debug:', {
    finalTrackId,
    dateFromUrl,
    hasAccess,
    isSubscribed,
    subscriptionLoading,
    activeSubscriptions: activeSubscriptions?.length || 0,
    requireSubscription,
    isPaywallTestUser,
    globalPaywallEnabled,
    shouldEnforcePaywall: finalShouldEnforcePaywall,
    originalShouldEnforcePaywall: shouldEnforcePaywall
  });

  // First check authentication using existing ProtectedRoute
  if (!isAuthenticated) {
    return <ProtectedRoute>{children}</ProtectedRoute>;
  }

  // If subscription check is not required, just render children
  if (!requireSubscription) {
    return <>{children}</>;
  }

  // Show loading state while checking subscription
  if (subscriptionLoading) {
    return (
      <MainLayout>
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-racing-blue mx-auto"></div>
            <p className="mt-4 text-gray-600">Checking subscription access...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  // If user doesn't have access to this track AND paywall is enforced (considering date), show paywall
  if ((!hasAccess || !isSubscribed) && finalShouldEnforcePaywall) {
    return <PaywallScreen finalTrackId={finalTrackId} hasAccess={hasAccess} isSubscribed={isSubscribed} activeSubscriptions={activeSubscriptions} location={location} />;
  }

  // User has access, render the protected content
  return <>{children}</>;
};

function PaywallScreen({ finalTrackId, hasAccess, isSubscribed, activeSubscriptions, location }: {
  finalTrackId?: string;
  hasAccess: boolean;
  isSubscribed: boolean;
  activeSubscriptions: unknown[];
  location: ReturnType<typeof useLocation>;
}) {
  const [promoOpen, setPromoOpen] = useState(false);
  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Premium Content</h1>
            <p className="text-lg text-gray-600 mb-6">
              {finalTrackId
                ? `Access to ${finalTrackId} tips requires an active subscription.`
                : 'This content requires an active subscription to view.'}
            </p>
          </div>

          <div className="bg-blue-50 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">🏇 Unlock Expert Racing Tips</h3>
            <p className="text-blue-700">
              Get access to professional analysis, track insights, and winning selections from our expert tipsters.
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => {
                const currentPath = location.pathname;
                window.location.href = `/subscription?from=${encodeURIComponent(currentPath)}&track=${encodeURIComponent(finalTrackId || '')}`;
              }}
              className="w-full bg-racing-blue text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Choose Your Package
            </button>
            <button
              onClick={() => setPromoOpen(true)}
              className="w-full border border-racing-green text-racing-green px-8 py-3 rounded-lg font-semibold hover:bg-racing-green hover:text-white transition-colors"
            >
              Redeem a Promo Code
            </button>
          </div>

          {process.env.NODE_ENV === 'development' && (
            <div className="mt-8 p-4 bg-gray-100 rounded text-left text-sm">
              <strong>Debug Info:</strong>
              <pre className="mt-2 text-xs">{JSON.stringify({ trackId: finalTrackId, hasAccess, isSubscribed, activeSubscriptions: activeSubscriptions?.length || 0 }, null, 2)}</pre>
            </div>
          )}
        </div>
      </div>
      <PromoCodeModal isOpen={promoOpen} onClose={() => setPromoOpen(false)} />
    </MainLayout>
  );
}

export default SubscriptionRoute;